import { create } from "zustand";
import { persist } from "zustand/middleware";

interface UserDetailsState {
  username: string | null;
  email: string | null;
  role: string | null;
  referralCode: string | null;
  primaryAirlineCode: string | null;
  profilePictureUrl: string | null;
  setUsername: (name: string | null) => void;
  setEmail: (email: string | null) => void;
  setUserRole: (role: string | null) => void;
  setReferralCode: (code: string | null) => void;
  setPrimaryAirlineCode: (code: string | null) => void;
  setProfilePictureUrl: (url: string | null) => void;
  resetUserDetails: () => void;
}

// Defaults live here so logout can restore them wholesale. Clearing the
// persisted "userDetails" key alone is NOT enough: logout navigates
// client-side, so the in-memory store survives and persist() writes it
// straight back on the next set() — leaking the previous user's details
// (notably their profile picture) into the next session.
const initialUserDetails = {
  username: "Username",
  email: null,
  role: null,
  referralCode: null,
  primaryAirlineCode: null,
  profilePictureUrl: null,
} as const;

export const useUserStore = create(
  persist<UserDetailsState>(
    (set) => ({
      ...initialUserDetails,
      setUsername: (name: string | null) => set({ username: name }),
      setEmail: (email: string | null) => set({ email: email }),
      setUserRole: (role: string | null) => set({ role: role }),
      setReferralCode: (code: string | null) => set({ referralCode: code }),
      setPrimaryAirlineCode: (code: string | null) => set({ primaryAirlineCode: code }),
      setProfilePictureUrl: (url: string | null) => set({ profilePictureUrl: url }),
      resetUserDetails: () => set({ ...initialUserDetails }),
    }),
    {
      name: "userDetails",
      // v1 drops any profilePictureUrl already written to localStorage. Logout
      // used to delete the persisted key without resetting the in-memory store,
      // so persist() wrote the previous user's picture straight back — browsers
      // that logged out under that build still hold a stale URL, which would
      // otherwise suppress the header's re-fetch indefinitely.
      version: 1,
      migrate: (persisted) =>
        ({ ...(persisted as object), profilePictureUrl: null }) as UserDetailsState,
    }
  )
);
